import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

/**
 * 一覧の「操作列」を右端に固定するための共通ロジック。
 *
 * design 正 (`design/css/components.css`。 2026-08-24 の差し替え後の現行) と同じ描き方:
 *
 * ```css
 * .ecl-table:has(td.cell-actions) thead th:last-child,
 * .ecl-table:has(td.cell-actions) tbody td.cell-actions {
 *     position: sticky; right: 0;
 *     overflow: visible;   ★ 画面ローカルの `.xxx-table td { overflow: hidden }` に
 *                            ::before ごと切り落とされるのを防ぐ
 *     --sticky-shade-w: 10px;
 *     background-color: transparent;   ★ 塗りは gradient の重ね (下記) で作る
 *     background-image:                   --sticky-overlay / --sticky-fill
 *         linear-gradient(var(--sticky-overlay, transparent), ...),
 *         linear-gradient(var(--sticky-fill, var(--surface-primary)), ...);
 *     background-position: var(--sticky-fill-x, var(--sticky-shade-w)) 0;   ★ 左端 10px を
 *     background-size: calc(100% - var(--sticky-fill-x, ...)) 100%;            切り欠いて透過
 * }
 * ...::before {
 *     content: ''; position: absolute; pointer-events: none;
 *     inset: 0 auto 0 0; width: var(--sticky-shade-w);   ★ 切り欠きに重なる セル "内側" 10px
 *     background: linear-gradient(to left, var(--shadow-sticky-edge), transparent);
 *                 ★ 濃い側 = 固定列の塗り際 (右)。透けた下層コンテンツの上に陰が落ちる
 * }
 * .ecl-table td.cell-actions {
 *     text-align: right;                ★ 列の右端寄せ (display:flex は行ズレのため禁止)
 *     padding-left: calc(var(--space-2) + var(--sticky-pad-x, var(--sticky-shade-w))) !important;
 *     padding-right: var(--space-2) !important;   ★ 見かけ 左右 8px 対称
 * }
 * .is-scrolled-end, .is-no-overflow { --sticky-fill-x: 0px }   ★ 影オフ時は切り欠きも畳む
 * .is-no-overflow { --sticky-pad-x: 0px }                     ★ overflow 無しなら pad も畳む
 * ```
 *
 * 固定列の塗りは **固定列が上のレイヤーに見える** ように、 左端 10px だけ切り欠いて透過させ、
 * 透けて見える下層 (横スクロールで流れる他の列) の上へ陰を落とす (2026-08-24 ユーザー指定、
 * issue #5489)。 セルの外へ影を落とす表現は、 `border-collapse` の表では作れない
 * (td への `box-shadow` は paint されず、 Chrome は sticky な td の外側を描画しない)。
 *
 * そのため **固定列の塗りを `background` / `backgroundColor` で直書きしてはいけない**。
 * 直書きすると切り欠きが塗り潰され、 陰が見えなくなる。 塗りは {@link stickyActionCellStyle} /
 * {@link stickyActionHeaderStyle} に色を渡し、 hover の重ね色は
 * {@link STICKY_ACTION_HOVER_OVERLAY} (`--sticky-overlay`) で載せる。
 *
 * `background: inherit` は使わない。 半透明の行背景 (`row-danger` 等の rgba) を二重合成して
 * 濃くする (design 側の実測 254,240,240 → 253,227,227)。
 *
 * `MasterDataTable` だけでなく、同じ見た目を持つ手組みテーブル
 * (`TradingPartnerTable` / `SupplyBilateral` の `TradeTable`)
 * からも使うためにここへ切り出す
 * (2026-08-06: 手組み側に反映が漏れて画面ごとに挙動が割れていたため共通化)。
 *
 * 新しく操作列を持つ表を足す時は、ここの関数を呼ぶ (= 3 つ目の自前実装を書かない)。
 * `MasterDataTable` を使える表なら `renderRowActions` に渡すだけで全部乗る。
 */

/**
 * 固定列の影の状態 ({@link useStickyRightShadow} が返し、 セルの `data-sticky-shadow` に載せる)。
 *
 * - `on`: 横に overflow していて右端まで scroll していない = 右にまだ列がある。 陰を出す
 * - `end`: 右端まで scroll 済。 陰と切り欠きを畳む。 左の余白 (pad) は畳まない
 *   (scroll 端で padding が変わると列幅が reflow し、 scrollWidth が揺れて on / end を行き来する)
 * - `off`: そもそも overflow していない。 陰・切り欠き・余白を畳み、 見かけ左右 8px に戻す
 *
 * design は同じ 3 状態を wrap の `is-scrolled-end` / `is-no-overflow` で持つ。
 */
export type StickyShadowState = 'on' | 'end' | 'off'

/** design `--sticky-shade-w`: 塗りを切り欠いて陰を落とす幅。 */
const STICKY_SHADE_WIDTH = '10px'

/** 固定列の見かけの左右 padding (design `--space-2`)。 */
const STICKY_ACTION_PADDING_INLINE = '8px'

/**
 * 陰の濃い側の色 = design token `--shadow-sticky-edge` (light。 `design/css/tokens.css`)。
 *
 * alpha 0.12 は issue #5489 の Des 裁定 (0.07 では macOS の overlay scrollbar が静止時に
 * 消える環境で「右にまだ列がある」 の手がかりとして弱すぎる)。
 * dark の値 (`rgba(255,255,255,0.10)`) は持たない。 dev は `src/index.tsx` の
 * `<Provider forcedTheme="light">` で light 固定のため。
 */
const STICKY_SHADOW_EDGE = 'rgba(0, 0, 0, 0.12)'

/**
 * 固定列の塗り。 左端 `--sticky-fill-x` (既定 10px) を切り欠いて、 hover の重ね色
 * (`--sticky-overlay`) と地の色 (`--sticky-fill`) の 2 層を敷く。
 *
 * `background` の shorthand 1 本で書く。 呼び出し側の style が `background` を先に持っていても
 * 後勝ちで置き換わり、 shorthand と longhand が混ざらない (React は混在を更新時に警告する)。
 */
function stickyFillStyle(fill: string): React.CSSProperties {
  const fillX = `var(--sticky-fill-x, ${STICKY_SHADE_WIDTH})`
  const layer = (color: string): string =>
    `linear-gradient(${color}, ${color}) ${fillX} 0 / calc(100% - ${fillX}) 100% no-repeat`
  return {
    ['--sticky-fill' as string]: fill,
    background: `${layer('var(--sticky-overlay, transparent)')}, ${layer('var(--sticky-fill)')}`,
    paddingLeft: `calc(${STICKY_ACTION_PADDING_INLINE} + var(--sticky-pad-x, ${STICKY_SHADE_WIDTH}))`,
    paddingRight: STICKY_ACTION_PADDING_INLINE,
  }
}

/**
 * 固定列の陰と、 状態ごとの切り欠き・余白の畳み方 (`~/constants/system.ts` の `globalCss` に載せる)。
 *
 * 陰は `::before` で描く。 `border-collapse` の表では td / th への `box-shadow` が paint されない
 * (実測: 固定セル左の画素が影なし時と 1 バイトも変わらない)。 `::before` はセルの内側
 * (切り欠いた 10px) に置くので、 上下の行や見出しへはみ出さない。
 *
 * `pointer-events: none` は必須: 擬似要素は絶対配置なので指定しないと
 * セル内の操作ボタンより手前で hit-test を奪う。
 *
 * セレクターは `th` / `td` に限る (`data-sticky-shadow` を表のセル以外に付けても効かせない)。
 */
export const STICKY_ACTION_SHADOW_GLOBAL_CSS = {
  ':is(th, td)[data-sticky-shadow="on"]::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    width: STICKY_SHADE_WIDTH,
    pointerEvents: 'none',
    background: `linear-gradient(to left, ${STICKY_SHADOW_EDGE}, transparent)`,
  },
  ':is(th, td)[data-sticky-shadow="end"]': {
    '--sticky-fill-x': '0px',
  },
  ':is(th, td)[data-sticky-shadow="off"]': {
    '--sticky-fill-x': '0px',
    '--sticky-pad-x': '0px',
  },
}

/** 操作ボタンが横に並ぶ時の間隔 (design `--space-2` = 8px)。chakra の gap token。 */
export const ROW_ACTION_GAP = 2

/**
 * 横スクロール container を監視して、固定列の影の状態 ({@link StickyShadowState}) を返す。
 *
 * design 側は `ecl-table-sticky-shadow.js` が「そもそも横 overflow 無し」
 * 「右端まで scroll 済」を wrap の class で持つ。同じ条件を scroll + ResizeObserver で再現する。
 *
 * @param deps 列幅が変わりうる値 (rows / columns / 展開状態 等)。変化時に再計算する。
 *   中身のサイズ変化は table 要素の `ResizeObserver` でも拾うので、deps は保険。
 */
export function useStickyRightShadow(deps: readonly unknown[] = []): {
  wrapRef: RefObject<HTMLDivElement>
  stickyShadow: StickyShadowState
} {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [stickyShadow, setStickyShadow] = useState<StickyShadowState>('off')

  const sync = useCallback((): void => {
    const wrap = wrapRef.current
    if (wrap === null) return
    // 1px の余裕で fractional pixel の丸め差を吸収する (design js と同じ)。
    const hasOverflow = wrap.scrollWidth > wrap.clientWidth + 1
    const atEnd = wrap.scrollLeft + wrap.clientWidth >= wrap.scrollWidth - 1
    setStickyShadow(!hasOverflow ? 'off' : atEnd ? 'end' : 'on')
  }, [])

  useEffect(() => {
    const wrap = wrapRef.current
    if (wrap === null) return undefined
    sync()
    wrap.addEventListener('scroll', sync, { passive: true })
    const observer = new ResizeObserver(sync)
    observer.observe(wrap)
    // wrap だけでなく **中身 (table)** も監視する。 行の展開 / 折りたたみでは wrap の
    // サイズは変わらず `scrollWidth` だけが変わるため、 wrap の observe では発火しない
    // (取引先タブの 2 階層展開で実際に起きる)。
    const content = wrap.firstElementChild
    if (content !== null) observer.observe(content)
    return () => {
      wrap.removeEventListener('scroll', sync)
      observer.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps は呼び出し側が渡す再計算トリガー
  }, [sync, ...deps])

  return { wrapRef, stickyShadow }
}

/**
 * 固定列 (body 側) の cell style。
 *
 * sticky cell は親 `tr` の背景を継承できず、透明のままだと下を流れる他列の文字が
 * 透けるため、zebra の色を cell 側の塗り (`--sticky-fill`) にも持たせる (design も同じ理由で
 * `tbody tr:nth-child(even) td.cell-actions { --sticky-fill: var(--surface-secondary) }`)。
 *
 * 陰は本 style に含めない。 呼び出し側が `data-sticky-shadow={stickyShadow}` を付け、
 * 描画は {@link STICKY_ACTION_SHADOW_GLOBAL_CSS} の `::before` が担う。
 *
 * @param rowIndex 0 始まりの行 index (zebra 判定に使う)
 */
export function stickyActionCellStyle(rowIndex: number): React.CSSProperties {
  return {
    position: 'sticky',
    right: 0,
    // sticky top の TH (z=1) より上、corner の TH (z=3) より下。
    zIndex: 2,
    ...stickyFillStyle(
      rowIndex % 2 === 1
        ? LightModeSemanticColors.Surface.secondary.value
        : LightModeSemanticColors.Surface.primary.value,
    ),
    textAlign: 'right',
    // 陰を描く `::before` が切り落とされないための保険 (design 正も同じガードを持つ:
    // `design/css/components.css` の「画面ローカルの `.xxx-table td { overflow: hidden }`
    // は ::before ごと切る」)。 操作列はボタンしか置かないので省略記号は不要。
    overflow: 'visible',
  }
}

/**
 * 固定列 (header 側 = sticky top と sticky right が交わる corner cell) の style。
 * body の sticky TD (z=2) より上に置く。
 *
 * `position: 'sticky'` は本 style に含める (呼び出し側が付け忘れると header だけ
 * 固定されず body の TD とズレるため)。 `top` は縦 sticky を持つ table 側だけが
 * 追加で指定する。
 *
 * 塗りは body 側と同じく切り欠き付きで敷くので、 見出しの地の色は `background` ではなく
 * 引数で渡す (呼び出し側の `background` は本 style が後から置き換える)。
 *
 * @param fill 見出しの地の色 (表ごとに異なる: header 色 / 白)。 sticky cell は透明のままだと
 *   下を流れる他列が透けるので必須。
 */
export function stickyActionHeaderStyle(fill: string): React.CSSProperties {
  return {
    position: 'sticky',
    right: 0,
    zIndex: 3,
    ...stickyFillStyle(fill),
    // body 側と同じ理由 (`::before` の clip 回避)。
    overflow: 'visible',
  }
}

/**
 * 行 hover 時に固定列へ重ねる色。
 *
 * hover token は半透明の overlay 用なので、 地の色 (`--sticky-fill`) の上に
 * 2 層目 (`--sticky-overlay`) として重ねる (design と同じ手法)。 `background` を直接
 * 差し替えると切り欠きと地の色が消える。
 */
export const STICKY_ACTION_HOVER_OVERLAY = {
  '& [data-sticky-action]': {
    '--sticky-overlay': LightModeSemanticColors.Hover.lowEmphasis.value,
  },
} as const
